"use client"

import { useState } from "react"
import type { ChassisImage } from "@lib/data/chassis-content"

/**
 * Photo selector for the chassis detail page. Renders a main image with a row
 * of thumbnail buttons below; clicking a thumb swaps the main image and updates
 * the caption. Falls back to a single static image if `images` has length 1.
 */
export default function ChassisGallery({
  images,
  alt,
}: {
  images: ChassisImage[]
  alt: string
}) {
  const [activeIndex, setActiveIndex] = useState(0)
  if (!images.length) return null
  const active = images[activeIndex] ?? images[0]

  return (
    <div>
      <figure style={{ margin: 0 }}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={active.url}
          alt={alt}
          style={{
            display: "block",
            maxWidth: "100%",
            width: "100%",
            borderRadius: 6,
            background: "#fafafa",
            border: "1px solid #e5e7eb",
            objectFit: "contain",
          }}
        />
        {active.caption && (
          <figcaption style={{ fontSize: 11, color: "#64748b", marginTop: 8 }}>
            {active.caption}
          </figcaption>
        )}
      </figure>

      {images.length > 1 && (
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          {images.map((img, i) => {
            const selected = i === activeIndex
            return (
              <button
                key={img.url}
                type="button"
                onClick={() => setActiveIndex(i)}
                aria-pressed={selected}
                title={img.label}
                style={{
                  flex: "0 1 110px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  padding: 4,
                  background: "#fff",
                  border: selected ? "2px solid #04414d" : "1px solid #e5e7eb",
                  borderRadius: 6,
                  cursor: "pointer",
                  outline: "none",
                  transition: "border-color .15s",
                }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={img.url}
                  alt={`${img.label} thumbnail`}
                  style={{
                    width: "100%",
                    aspectRatio: "16 / 9",
                    objectFit: "contain",
                    background: "#fafafa",
                    borderRadius: 3,
                  }}
                />
                <span
                  style={{
                    fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                    fontSize: 10,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: selected ? "#04414d" : "#475569",
                    fontWeight: selected ? 700 : 500,
                  }}
                >
                  {img.label}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
